import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { usePost } from '../API';
import type { GetProgramHomeResult } from '@aidkitorg/aidkit/lib/admin_panel/programHome';
import { logAdminPanelError } from '../utils/adminPanel.utils';

type ProgramHomeContextType = {
  programHomeData: GetProgramHomeResult | null,
  programHomeDataTimestamp?: number,

  isProgramHomeLoading: boolean,

  fetchProgramHome: (force?: boolean) => void,
}

type ProgramHomeDataRef = {
  data?: GetProgramHomeResult,
  timestamp?: number,
  expiresAt?: number,
}

type ProgramHomeProviderProps = {
  children: React.ReactNode
}

const ProgramHomeContext = createContext<ProgramHomeContextType>({
  programHomeData: null,

  isProgramHomeLoading: false,

  fetchProgramHome: async (_force?: boolean) => {},
})

export const useProgramHome = () => {
  const context = useContext(ProgramHomeContext)
  if (!context) {
    throw new Error("useProgramHome must be used within a ProgramHomeProvider")
  }

  return context;
}

export const ProgramHomeProvider: React.FC<ProgramHomeProviderProps> = ({ children }) => {
  const [programHomeData, setProgramHomeData] = useState<GetProgramHomeResult | null>(null)
  const [programHomeDataTimestamp, setProgramHomeDataTimestamp] = useState<number>()

  const [isProgramHomeLoading, setIsProgramHomeLoading] = useState(false);

  const programHomeDataRef = useRef<ProgramHomeDataRef>({})

  const getProgramHomeData = usePost("/api/admin_panel/home")

  const fetchProgramHome = useCallback(async (force?: boolean) => {
    const now = Date.now()

    // Check if an unexpired value lives in the cache
    if (!force &&
      programHomeDataRef.current.data &&
      (programHomeDataRef.current.expiresAt && now < programHomeDataRef.current.expiresAt))
    {
      return
    }

    setIsProgramHomeLoading(true)
    const result = await getProgramHomeData({ force })
    setIsProgramHomeLoading(false)

    if (result && result.success && result.data) {
      programHomeDataRef.current = {
        data: result.data,
        timestamp: Date.now(),
        expiresAt: Date.now() + (5 * 60 * 1000) // Cache for 5 minutes
      }

      setProgramHomeData(result.data)
      setProgramHomeDataTimestamp(Date.now())
    } else if (result.error) {
      logAdminPanelError(result.error, result.message)
    }
  }, [getProgramHomeData])

  const value = {
    programHomeData,
    programHomeDataTimestamp,

    isProgramHomeLoading,

    fetchProgramHome,
  }

  return (
    <ProgramHomeContext.Provider value={ value }>
      {children}
    </ProgramHomeContext.Provider>
  )
}
